// Build-mode header for the wheel dashboard's right column. Rendered ONLY by WheelDashboard, above
// the inline node editor — never by WheelModal, which is what keeps edit controls off patient screens.
// It carries what the editor cannot: where you are in the wheel, and what you can do to the node.
import React, { useState } from "react";
import { Button, Popconfirm, Switch, Tooltip } from "antd";
import { BookOutlined, BranchesOutlined, CheckOutlined, CloudUploadOutlined, CommentOutlined, DatabaseOutlined, DeleteOutlined, EyeInvisibleOutlined, EyeOutlined, FormOutlined, InboxOutlined, LockOutlined, PlusOutlined, RollbackOutlined, WarningOutlined } from "@ant-design/icons";

import type { PanelLifecycleVerb } from "../../../../api/geneCodes/PanelApi";
import { HiddenReason, Panel, PanelStatus } from "../../../../types/geneCodes/Panel";
import { Trait } from "../../../../types/geneCodes/TraitTypes";
import { Staging } from "../../../../types/geneCodes/StagingTypes";
import { WheelNodeKind, childKindOf, isDraftPanel, isHiddenPanel, kindLabel, panelStatusOf } from "../../../../utils/geneCodes/wheelAuthoring";
import { GeneRingEntry, geneRingForTrait } from "../../../../utils/geneCodes/wheelGeneRing";
import { useSymbolResolution } from "./useSymbolResolution";
import "./wheelBuilder.css";

export interface GeneDeleteTarget {
	wheelGeneId: number;
	name: string;
	snpCount: number;
}

// A gene is addressed by its row alone: its PATCH must carry nothing but is_hidden.
export type NodeVisibilityTarget = { kind: "trait"; trait: Trait } | { kind: "gene"; wheelGeneId: number } | { kind: "snp"; snp: Staging };

// labiq computes the reason (D21); this only says it.
const HIDDEN_REASON_TEXT: Record<HiddenReason, string> = {
	hidden: "Hidden from practitioners and patients",
	trait_hidden: "Not shown — its trait is hidden",
	gene_hidden: "Not shown — its gene is hidden",
	low_quality: "Not shown — quality is Low (0) or not set. Set quality to 1 to show it.",
	no_visible_snps: "Not shown — none of its SNPs are shown",
};

interface NodeVisibilityProps {
	/** Null for a gene with no wheel_genes row: there is nothing to save the flag on. */
	target: NodeVisibilityTarget | null;
	hidden: boolean;
	reason: HiddenReason | null;
	onSetHidden: (target: NodeVisibilityTarget, hidden: boolean) => Promise<boolean>;
}

// Keyed by node and flag at the call site, so fresh server data resets the pending value.
const NodeVisibility: React.FC<NodeVisibilityProps> = ({ target, hidden, reason, onSetHidden }) => {
	const [pending, setPending] = useState<boolean | null>(null);
	const [saving, setSaving] = useState(false);

	const handleChange = async (visible: boolean) => {
		if (!target) return;
		setPending(!visible);
		setSaving(true);
		const saved = await onSetHidden(target, !visible);
		setSaving(false);
		if (!saved) setPending(null);
	};

	const toggle = (
		<Switch
			size="small"
			checked={!(pending ?? hidden)}
			loading={saving}
			disabled={saving || !target}
			onChange={handleChange}
			aria-label="Visible to practitioners and patients"
			data-testid="wheel-inspector-visible-switch"
		/>
	);

	return (
		<div className="mg-wheel-inspector__visibility" data-testid="wheel-inspector-visibility">
			<div className="mg-wheel-inspector__visibility-row">
				<span className="mg-wheel-inspector__visibility-label">Visible to practitioners and patients</span>
				{/* A disabled switch swallows hover, so the tooltip needs a wrapper to hang off. */}
				{target ? toggle : (
					<Tooltip title="Save this gene to the wheel first">
						<span data-testid="wheel-inspector-visible-locked">{toggle}</span>
					</Tooltip>
				)}
			</div>
			{reason && HIDDEN_REASON_TEXT[reason] ? (
				<span className="mg-wheel-inspector__visibility-reason" data-testid="wheel-inspector-visibility-reason">
					{HIDDEN_REASON_TEXT[reason]}
				</span>
			) : null}
		</div>
	);
};

interface LifecycleControl {
	action: PanelLifecycleVerb;
	label: string;
	okText: string;
	description: string;
	icon: React.ReactNode;
	danger?: boolean;
}

// Only the transitions the server will actually accept for a status are offered — a draft has no
// Unpublish because there is nothing to take back, and the descriptions are the blast radius said
// BEFORE the call, not after it.
const LIFECYCLE_CONTROLS: Record<PanelStatus, LifecycleControl[]> = {
	draft: [
		{
			action: "publish",
			label: "Publish",
			okText: "Publish",
			icon: <CloudUploadOutlined />,
			description: "Publishing is not a status flip: this draft takes over the live wheel's name, its traits and SNPs are renamed with it, the version it was forked from is archived, and every sample holding that version moves onto this one.",
		},
	],
	published: [
		{
			action: "unpublish",
			label: "Unpublish",
			okText: "Unpublish",
			icon: <RollbackOutlined />,
			description: "Returns this wheel to draft under the same name — nothing is renamed and no assignment moves. Patients it is assigned to keep that assignment but stop seeing the wheel until you publish it again.",
		},
		{
			action: "hide",
			label: "Hide",
			okText: "Hide",
			icon: <EyeInvisibleOutlined />,
			description: "Hides this wheel from every practitioner and patient screen and from Xoe. Assignments are kept; patients see it again when you unhide it.",
		},
		{
			action: "archive",
			label: "Archive",
			okText: "Archive",
			icon: <InboxOutlined />,
			danger: true,
			description: "Retires the wheel without replacing it — every patient holding it stops seeing it. Restore reopens it as a draft.",
		},
		{
			action: "fork",
			label: "Fork to draft",
			okText: "Fork",
			icon: <BranchesOutlined />,
			description: "Copies this wheel, its traits and its SNPs into a new draft under a (draft) name. The live version keeps serving patients until that draft is published.",
		},
	],
	hidden: [
		{
			action: "unhide",
			label: "Unhide",
			okText: "Unhide",
			icon: <EyeOutlined />,
			description: "Shows this wheel again to practitioners and to every patient it is assigned to.",
		},
		{
			action: "archive",
			label: "Archive",
			okText: "Archive",
			icon: <InboxOutlined />,
			danger: true,
			description: "Retires the wheel without replacing it — it can no longer be assigned to a sample. Restore reopens it as a draft.",
		},
		{
			action: "fork",
			label: "Fork to draft",
			okText: "Fork",
			icon: <BranchesOutlined />,
			description: "Copies this wheel, its traits and its SNPs into a new draft under a (draft) name. Publishing that draft keeps it hidden and moves this wheel's assignments onto it.",
		},
	],
	archived: [
		{
			action: "restore",
			label: "Restore as draft",
			okText: "Restore",
			icon: <RollbackOutlined />,
			description: "Reopens the wheel as a draft, keeping its archived name. It stays off patient screens until it is published.",
		},
	],
};

interface WheelInspectorProps {
	panel: Panel | null;
	trait: Trait | null;
	gene: string | null;
	snp: Staging | null;
	snps: Staging[];
	onAddNode: (kind: WheelNodeKind) => void;
	onPickSnps: () => void;
	/** Which PANE the column shows — not the per-node edit modal S5 removed. Optional so a shell that mounts no editor offers no way to it. */
	onOpenEdit?: () => void;
	onOpenResearch: () => void;
	onOpenXoe: () => void;
	/** A5, optional on purpose — only WheelDashboard supplies it, so no other shell can render the delete. */
	onDeleteGene?: (target: GeneDeleteTarget) => void;
	/** R5, optional for the same reason — the lifecycle never reaches a patient-facing shell. */
	onLifecycle?: (action: PanelLifecycleVerb) => void;
	/** Optional for the same reason; resolves false when the save was refused. */
	onSetNodeHidden?: (target: NodeVisibilityTarget, hidden: boolean) => Promise<boolean>;
}

interface VisibilityState {
	key: string;
	target: NodeVisibilityTarget | null;
	hidden: boolean;
	reason: HiddenReason | null;
}

// The switch shows the node's OWN flag; the reason line says why viewers do not see it.
const visibilityOf = (kind: WheelNodeKind, trait: Trait | null, gene: string | null, snp: Staging | null, geneEntry: GeneRingEntry<Staging> | null): VisibilityState | null => {
	if (kind === "snp" && snp) return { key: `snp:${snp.idstaging}`, target: { kind: "snp", snp }, hidden: Boolean(snp.is_hidden), reason: snp.hidden_reason ?? null };
	if (kind === "trait" && trait) return { key: `trait:${trait.idtraits}`, target: { kind: "trait", trait }, hidden: Boolean(trait.is_hidden), reason: trait.hidden_reason ?? null };
	if (kind !== "gene" || !gene) return null;

	const wheelGeneId = geneEntry?.wheelGeneId ?? null;
	const row = wheelGeneId != null ? trait?.genes?.find((ref) => ref.id === wheelGeneId) : undefined;
	return { key: `gene:${wheelGeneId ?? gene}`, target: wheelGeneId != null ? { kind: "gene", wheelGeneId } : null, hidden: Boolean(row?.is_hidden), reason: row?.hidden_reason ?? null };
};

export const selectedKind = (trait: Trait | null, gene: string | null, snp: Staging | null): WheelNodeKind => {
	if (snp) return "snp";
	if (gene) return "gene";
	if (trait) return "trait";
	return "panel";
};

const WheelInspector: React.FC<WheelInspectorProps> = ({ panel, trait, gene, snp, snps, onAddNode, onPickSnps, onOpenEdit, onOpenResearch, onOpenXoe, onDeleteGene, onLifecycle, onSetNodeHidden }) => {
	const kind = selectedKind(trait, gene, snp);
	const addKind = childKindOf(kind);

	// The ONE ring derivation (wheelGeneRing.ts) — the chips list the same genes, in the same order, as the wheel's ring.
	const ring = trait ? geneRingForTrait(trait, snps) : [];
	const geneEntry = kind === "gene" && gene ? ring.find((entry) => entry.name === gene) ?? null : null;
	// A5: the record view exists only for a gene with a server row; resolution is asked only there.
	const geneRecord = panel && geneEntry?.wheelGeneId != null ? geneEntry : null;
	const resolution = useSymbolResolution(geneRecord ? geneRecord.name : null);

	if (!panel) return null;

	const crumbs = [panel.name_panel, trait?.trait_name, gene, snp?.rs].filter((crumb): crumb is string => Boolean(crumb));
	const traitGenes = ring.map((entry) => entry.name);
	// The wheel's own lifecycle, so it is offered on the wheel node and nowhere below it.
	// `?? []`: a status this build has never seen offers nothing instead of crashing the builder.
	const lifecycle = kind === "panel" && onLifecycle ? LIFECYCLE_CONTROLS[panelStatusOf(panel)] ?? [] : [];
	const visibility = visibilityOf(kind, trait, gene, snp, geneEntry);

	return (
		<div className="mg-wheel-inspector" data-testid="wheel-inspector">
			<div className="mg-wheel-inspector__head">
				<div className="mg-wheel-inspector__crumbs">
					{crumbs.map((crumb, index) => (
						<React.Fragment key={crumb}>
							{index > 0 ? <span className="mg-wheel-inspector__crumb-sep">›</span> : null}
							<span className={index === crumbs.length - 1 ? "mg-wheel-inspector__crumb mg-wheel-inspector__crumb--current" : "mg-wheel-inspector__crumb"}>{crumb}</span>
						</React.Fragment>
					))}
				</div>
			</div>

			<div className="mg-wheel-inspector__actions">
				{addKind ? (
					<Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => onAddNode(addKind)} data-testid="wheel-inspector-add">
						Add {kindLabel(addKind)}
					</Button>
				) : null}

				{/* The picker attaches SNPs to the TRAIT, so it stays there too — R6 only moved the add. */}
				{addKind === "snp" || kind === "trait" ? (
					<Button size="small" icon={<DatabaseOutlined />} onClick={onPickSnps} data-testid="wheel-inspector-pick">
						Add from catalogue
					</Button>
				) : null}

				{geneRecord && onDeleteGene ? (
					<Popconfirm
						title={`Delete ${geneRecord.name}?`}
						// The blast radius, BEFORE anything is called: the count is the node's own SNPs.
						description={
							geneRecord.snps.length === 0
								? `Removes the node from ${trait?.trait_name ?? "this trait"}. It has no SNPs, so nothing else changes.`
								: `Detaches ${geneRecord.snps.length} ${geneRecord.snps.length === 1 ? "SNP" : "SNPs"} from ${trait?.trait_name ?? "this trait"} — they leave the ring with it.`
						}
						okText="Delete"
						okButtonProps={{ danger: true, "data-testid": "wheel-inspector-gene-delete-confirm" } as never}
						cancelText="Cancel"
						onConfirm={() => onDeleteGene({ wheelGeneId: geneRecord.wheelGeneId as number, name: geneRecord.name, snpCount: geneRecord.snps.length })}
					>
						<Button size="small" danger icon={<DeleteOutlined />} data-testid="wheel-inspector-gene-delete">
							Delete gene
						</Button>
					</Popconfirm>
				) : null}

				{/* Edit / Research / Ask Xoe are three PEERS naming the pane the column shows — NOT the
				    per-node edit modal S5 removed. None reacts to which pane is open, so the row never shifts. */}
				{onOpenEdit ? (
					<Button size="small" icon={<FormOutlined />} onClick={onOpenEdit} data-testid="wheel-inspector-open-edit">
						Edit
					</Button>
				) : null}

				<Button size="small" icon={<BookOutlined />} onClick={onOpenResearch} data-testid="wheel-inspector-research">
					Research
				</Button>

				<Button size="small" icon={<CommentOutlined />} onClick={onOpenXoe} data-testid="wheel-inspector-xoe">
					Ask Xoe
				</Button>

				{lifecycle.map((control) => (
					<Popconfirm
						key={control.action}
						title={`${control.label} ${panel.name_panel}?`}
						description={control.description}
						okText={control.okText}
						okButtonProps={{ danger: control.danger, "data-testid": `wheel-inspector-${control.action}-confirm` } as never}
						cancelText="Cancel"
						onConfirm={() => onLifecycle?.(control.action)}
					>
						<Button size="small" danger={control.danger} icon={control.icon} data-testid={`wheel-inspector-${control.action}`}>
							{control.label}
						</Button>
					</Popconfirm>
				))}
			</div>

			{visibility && onSetNodeHidden ? (
				<NodeVisibility key={`${visibility.key}:${visibility.hidden}`} target={visibility.target} hidden={visibility.hidden} reason={visibility.reason} onSetHidden={onSetNodeHidden} />
			) : null}

			{trait && !gene && !snp ? (
				<div className="mg-wheel-inspector__genes">
					<div className="mg-wheel-inspector__eyebrow">Genes in this trait</div>
					<div className="mg-wheel-inspector__chips">
						{traitGenes.map((name) => (
							<span key={name} className="mg-wheel-inspector__chip" data-testid="wheel-inspector-gene">
								{name}
							</span>
						))}
					</div>
					<span className="mg-wheel-inspector__note">Add a gene to give this trait a node of its own; its SNPs go under it.</span>
				</div>
			) : null}

			{geneRecord && !snp ? (
				<div className="mg-wheel-inspector__gene-record" data-testid="wheel-inspector-gene-record">
					<div className="mg-wheel-inspector__record-row">
						<span className="mg-wheel-inspector__eyebrow">SNPs in this trait</span>
						<span className="mg-wheel-inspector__record-value" data-testid="wheel-inspector-gene-snp-count">
							{geneRecord.snps.length}
						</span>
					</div>

					{resolution && resolution.state !== "loading" ? (
						<div className={`mg-wheel-inspector__registry mg-wheel-inspector__registry--${resolution.state}`} data-testid="wheel-inspector-registry" data-state={resolution.state}>
							{resolution.state === "resolved" ? (
								<span>
									<CheckOutlined /> In the registry · {resolution.gene.id}
								</span>
							) : resolution.state === "alias" ? (
								<>
									<span>
										<WarningOutlined /> {geneRecord.name} is not a current registry symbol — it matches:
									</span>
									{resolution.candidates.map((candidate) => (
										<span key={candidate.gene.id} className="mg-wheel-inspector__registry-candidate" data-testid="wheel-inspector-registry-candidate">
											{candidate.gene.symbol}
											{candidate.via ? ` — via ${candidate.via}` : ""}
										</span>
									))}
								</>
							) : resolution.state === "unresolved" ? (
								<span>
									<WarningOutlined /> {geneRecord.name} is not in the registry.
								</span>
							) : (
								// The S10 rule: an outage is UNKNOWN (lock + amber) — never "resolved", never blocking.
								<span>
									<LockOutlined /> Registry unavailable — this symbol could not be checked.
								</span>
							)}
						</div>
					) : null}
				</div>
			) : null}

			{gene && !snp && !geneRecord ? (
				<>
					<span className="mg-wheel-inspector__note" data-testid="wheel-inspector-gene-note">
						This gene came from the SNPs naming it, not from a gene record. Add or remove SNPs to change it.
					</span>
					<span className="mg-wheel-inspector__note" data-testid="wheel-inspector-gene-backfill-hint">
						This gene has no server id yet — rename, registry link and reorder arrive once the wheel-genes backfill has run.
					</span>
				</>
			) : null}

			{isDraftPanel(panel) ? (
				<div className="mg-wheel-inspector__draft" data-testid="wheel-inspector-draft">
					<WarningOutlined />
					<span>This wheel is a draft. Nothing here is visible to patients, and it cannot be assigned to a sample until you publish it.</span>
				</div>
			) : null}

			{isHiddenPanel(panel) ? (
				<div className="mg-wheel-inspector__hidden" data-testid="wheel-inspector-hidden">
					<EyeInvisibleOutlined />
					<span>Hidden from practitioners and patients. It stays assigned to samples and reappears when unhidden. Report PDFs already generated are not changed.</span>
				</div>
			) : null}
		</div>
	);
};

export default WheelInspector;
